import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { calendarService } from '../../../services/calendarService';
import { addWeeks, setHours, setMinutes, setSeconds, setMilliseconds, addHours } from 'date-fns';

const BulkSlotCreator = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    startDate: '',
    numberOfWeeks: 4,
    timeSlots: [
      { hours: 10, minutes: 0, enabled: true },
      { hours: 11, minutes: 30, enabled: true },
      { hours: 14, minutes: 0, enabled: true },
      { hours: 15, minutes: 30, enabled: true }
    ]
  });

  const handleToggleTimeSlot = (index) => {
    setFormData(prev => ({
      ...prev,
      timeSlots: prev?.timeSlots?.map((slot, i) => 
        i === index ? { ...slot, enabled: !slot?.enabled } : slot
      )
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.startDate) {
      setError('Please select a start date');
      return;
    }

    const enabledSlots = formData?.timeSlots?.filter(slot => slot?.enabled);
    if (enabledSlots?.length === 0) {
      setError('Please select at least one time slot');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const slots = [];
      const startDate = new Date(formData?.startDate);
      
      // Generate slots for specified number of weeks
      for (let week = 0; week < formData?.numberOfWeeks; week++) {
        const targetDate = addWeeks(startDate, week);
        
        for (const timeSlot of enabledSlots) {
          let startTime = setHours(targetDate, timeSlot?.hours);
          startTime = setMinutes(startTime, timeSlot?.minutes);
          startTime = setSeconds(startTime, 0);
          startTime = setMilliseconds(startTime, 0);
          
          const endTime = addHours(startTime, 1);
          
          await calendarService?.createAvailabilitySlot({
            startTime: startTime?.toISOString(),
            endTime: endTime?.toISOString()
          });
          
          slots?.push({ startTime, endTime });
        }
      }

      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to create slots');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeSlot = (hours, minutes) => {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    const displayMinutes = minutes?.toString()?.padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-elevation-3 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Plus" size={24} className="text-primary" />
              Bulk Slot Creator
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Create multiple consultation slots with customizable time patterns
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Start Date */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground mb-2">
              Start Date (Monday)
            </label>
            <input
              type="date"
              value={formData?.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e?.target?.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Select the first Monday to start generating slots
            </p>
          </div>

          {/* Number of Weeks */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground mb-2">
              Number of Weeks
            </label>
            <input
              type="number"
              min="1"
              max="12"
              value={formData?.numberOfWeeks}
              onChange={(e) => setFormData(prev => ({ ...prev, numberOfWeeks: parseInt(e?.target?.value) }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Generate slots for the next 1-12 weeks
            </p>
          </div>

          {/* Time Slots Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground mb-3">
              Time Slots (Select which times to include)
            </label>
            <div className="space-y-3">
              {formData?.timeSlots?.map((slot, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    slot?.enabled 
                      ? 'border-primary bg-primary/5' :'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={slot?.enabled}
                      onChange={() => handleToggleTimeSlot(index)}
                      className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {formatTimeSlot(slot?.hours, slot?.minutes)} - {formatTimeSlot(slot?.hours + 1, slot?.minutes)}
                      </p>
                      <p className="text-xs text-muted-foreground">60-minute consultation slot</p>
                    </div>
                  </div>
                  <Icon 
                    name={slot?.enabled ? 'CheckCircle' : 'Circle'} 
                    size={20} 
                    className={slot?.enabled ? 'text-primary' : 'text-gray-400'}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="mb-6 bg-[#E6F0F5] border border-[#002B45]/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={16} className="text-[#002B45] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[#002B45] mb-1">Generation Summary</p>
                <p className="text-xs text-[#002B45]/80">
                  This will create {formData?.timeSlots?.filter(s => s?.enabled)?.length} slot(s) per week
                  for {formData?.numberOfWeeks} week(s), totaling {formData?.timeSlots?.filter(s => s?.enabled)?.length * formData?.numberOfWeeks} consultation slots.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={loading}
              disabled={loading}
              iconName="Plus"
              iconPosition="left"
            >
              Create Slots
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkSlotCreator;